import { useState, useEffect, useMemo } from 'react';
import MapCustom, {
  IMapConfig,
  IParamsMapCustomProps,
} from './components/MapCustom';
import { initialCompany } from '../company/types/company.type';
import useMapSetting, { EMapType } from '../../zustand/useMap';
import { httpRequest } from '../../helpers/api';
import { generateQueryString, getErrorMessage } from '@qlibs/react-components';
import dayjs from 'dayjs';
import Map from './components/Map';
import { IAE, IGps, IRpm } from '../report/types/report-log.type';
import { BaseResponseProps } from '../../types/config.type';
import { useSearchParams } from 'react-router-dom';
import useAuthApp from '../../hooks/useAuthApp';

interface IParamsHomeMapCustomProps extends Partial<IParamsMapCustomProps> {
  assetIds: string[];
}

export type LastDataLogProps = {
  [key: string]: {
    assetId: string;
    name: string;
    gps: {
      [key: string]: IGps;
    };
    rpm: {
      [key: string]: IRpm;
    };
    ae1: {
      [key: string]: IAE;
    };
    ae2: {
      [key: string]: IAE;
    };
    ae3: {
      [key: string]: IAE;
    };
  };
};

const initialMapConfig: IMapConfig = {
  mapType: EMapType.satellite,
  isShowMapCoordinate: false,
  isShowMapAttribution: false,
  isShowMapControl: false,
  isShowMapLayers: true,
  isShowMapPolyline: false,
  isShowMapSettings: false,
  showVesselOnline: true,
  showVesselOffline: true,
  showVesselWaiting: true,
  showVesselLabel: true,
  showVesselDotTrail: false,
  trailWidth: 5,
  trailOpacity: 1,
  zoom: 9,
  showTimezoneLayer: false,
  showWindLayer: false,
  showWaveLayer: false,
  activeLayers: '',
};

const HomeCustom: React.FC = () => {
  const { signInUsingToken } = useAuthApp();
  const [mapConfig, setMapConfig] = useState<IMapConfig>({
    ...initialMapConfig,
  });
  const [lastDataLog, setLastDataLog] = useState<LastDataLogProps>();
  const [loadingLastDataLog, setLoadingLastDataLog] = useState<boolean>(false);
  const {
    setShowTimezoneLayer,
    setShowWindLayer,
    setShowWaveLayer,
    setActiveLayer,
  } = useMapSetting();
  const [searchParams, setSearchParams] = useSearchParams();

  const accessToken = searchParams.get('accessToken');
  const companyId = searchParams.get('companyId');
  const assetIds = searchParams.get('assetIds');
  const showAssetsIds = searchParams.get('showAssetsIds');

  useEffect(() => {
    const handleParamsLoaded = (event: any) => {
      const currentMapConfig = JSON.parse(event.data);
      setMapConfig({
        ...currentMapConfig,
      });
    };
    document.addEventListener('message', handleParamsLoaded);
    return () => {
      document.removeEventListener('message', handleParamsLoaded);
    };
  }, []);

  const getReportLogDevice = async (
    customParams: {
      deviceType: string;
      interval: string;
      assetIds: string;
      trailLength: number;
    },
    showDotTrail?: boolean,
  ) => {
    try {
      setLoadingLastDataLog(true);

      const newFilter = {
        deviceType: showDotTrail ? 'gps' : customParams.deviceType,
        interval: customParams.interval,
        lastDataLimit:
          showDotTrail && customParams.trailLength
            ? customParams.trailLength * 60
            : 1,
        assetIds: customParams.assetIds,
      };

      const results = await httpRequest.get<
        BaseResponseProps<LastDataLogProps>
      >(`/report-log/last-data/by-assets${generateQueryString(newFilter)}`);

      if (showDotTrail) {
        const newData = results.data.payload;
        const newLastDataLog = { ...lastDataLog };

        for (const device in newData) {
          if (
            newData.hasOwnProperty(device) &&
            newLastDataLog.hasOwnProperty(device)
          ) {
            const newGpsEntries = newData[device]['gps'];
            const originalGpsEntries = newLastDataLog[device]['gps'];

            if (Object.keys(newLastDataLog).length > 1) {
              newLastDataLog[device]['gps'] = {
                // ...originalGpsEntries,
                ...newGpsEntries,
              };
            } else {
              newLastDataLog[device]['gps'] = {
                ...originalGpsEntries,
                ...newGpsEntries,
              };
            }
          }
        }
        setLastDataLog(newLastDataLog);
      } else {
        setLastDataLog(results.data.payload);
      }
    } catch (error) {
      console.error(getErrorMessage(error));
    } finally {
      setLoadingLastDataLog(false);
    }
  };

  const handleFetchLastData = async (data: {
    accessToken: string;
    assetIds: string;
    trailLength: number;
    showVesselDotTrail: boolean;
  }) => {
    await signInUsingToken(data.accessToken);
    await getReportLogDevice(
      {
        deviceType: 'gps,rpm,ae-1,ae-2,ae-3',
        interval: 'minute',
        assetIds: data.assetIds,
        trailLength: data.trailLength,
      },
      data.showVesselDotTrail,
    );
  };

  useEffect(() => {
    if (accessToken && assetIds?.length) {
      handleFetchLastData({
        accessToken,
        assetIds,
        trailLength: mapConfig.trailLength || 24,
        showVesselDotTrail: mapConfig.showVesselDotTrail,
      });

      const pollingInterval = setInterval(() => {
        getReportLogDevice(
          {
            deviceType: 'gps,rpm,ae-1,ae-2,ae-3',
            interval: 'minute',
            assetIds: assetIds,
            trailLength: mapConfig.trailLength || 24,
          },
          mapConfig.showVesselDotTrail,
        );
      }, 30000);

      return () => clearInterval(pollingInterval);
    }
  }, [
    accessToken,
    assetIds,
    mapConfig.showVesselDotTrail,
    mapConfig.trailLength,
  ]);

  useEffect(() => {
    setShowTimezoneLayer(mapConfig.showTimezoneLayer || false);
  }, [mapConfig.showTimezoneLayer]);

  useEffect(() => {
    setShowWindLayer(mapConfig.showWindLayer || false);
  }, [mapConfig.showWindLayer]);

  useEffect(() => {
    setShowWaveLayer(mapConfig.showWaveLayer || false);
  }, [mapConfig.showWaveLayer]);

  useEffect(() => {
    setActiveLayer(mapConfig.activeLayers || '');
  }, [mapConfig.activeLayers]);

  return (
    <>
      <MapCustom
        isMobile
        loadingData={loadingLastDataLog}
        companyId={companyId || ''}
        mapConfig={mapConfig}
        lastDataLog={lastDataLog}
        showAssetsIds={showAssetsIds ? showAssetsIds.split(',') : []}
      />
    </>
  );
};

export default HomeCustom;
